import { ResetPassword } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { CircleLoader } from '@/app/common/components/CircleLoader'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { EyeIcon, EyeSlashIcon } from '@/app/common/icons/general'
import { Button } from '@/common/components/Button.tsx'
import { Input } from '@/common/components/Input.tsx'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { AppRoutePaths } from '../../../../routing/route_paths.ts'
import { AuthLayout } from '../layout.tsx'

export default function ResetPasswordPage() {
  const [passwordChanged, setPasswordChanged] = useState(false)

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-5">
        {!passwordChanged && (
          <PasswordResetForm setPasswordChanged={setPasswordChanged} />
        )}
        {passwordChanged && <PasswordChangedView />}
      </AuthLayout.Content>
    </AuthLayout>
  )
}

interface PasswordResetFormProps {
  setPasswordChanged: (value: boolean) => void
}

const ResetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: 'password_must_be_at_least_characters_long' })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/, {
        message: 'password_must_contain_at_least_one_uppercase_letter',
      }),
    confirm_password: z.string().min(8, {
      message: 'confirm_password_must_be_at_least_characters_long',
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'passwords_do_not_match',
    path: ['confirm_password'],
  })
type ResetPasswordFormProps = z.infer<typeof ResetPasswordSchema>

const PasswordResetForm = ({ setPasswordChanged }: PasswordResetFormProps) => {
  const { t: translate } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { fireAlert } = useAppAlert()
  const params = new URLSearchParams(location.search)
  const navigate = useNavigate()
  const [resetParams, setResetParams] = useState<null | QueryInvitation>(null)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  useEffect(() => {
    console.log(params.get("q"))
    decodeBase64Invitation(params.get('q') ?? '')
      .then((qInvitation) => {
        setResetParams(qInvitation)
      })
      .catch((reason) => {
        console.error(reason)
        fireAlert({
          variant: 'error',
          message: 'Invalid request',
        }).then(() => navigate(AppRoutePaths.auth.login))
      })
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormProps>({
    resolver: zodResolver(ResetPasswordSchema),
  })

  const onSubmit: SubmitHandler<ResetPasswordFormProps> = async (formData) => {
    setLoading(true)
    const { error } = await ResetPassword({
      user_id: resetParams?.user_id ?? '0',
      token: resetParams?.token ?? '',
      payload: {
        password: formData.password,
      },
    })
    setLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      setPasswordChanged(true)
    }
  }
  return (
    <>
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          {translate('reset_password')}
        </h3>
        <span className="text-sm text-gray-500 font-medium">
          {translate('enter_your_new_password')}
        </span>
      </div>
      <form noValidate className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            {translate('new_password')}
          </label>
          <div className="mt-2 relative flex">
            <Input
              {...register('password')}
              type={showNewPassword ? 'text' : 'password'}
              required
              placeholder={translate('enter_your_new_password')}
            />
            <div
              className="absolute right-0 flex items-center h-full mr-2"
              onClick={() => {
                setShowNewPassword(!showNewPassword)
              }}
            >
              {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </div>
          </div>
        </div>
        <FormErrorMessage message={translate(errors.password?.message ?? '')} />

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            {translate('confirm_new_password')}
          </label>
          <div className="mt-2 relative flex">
            <Input
              {...register('confirm_password')}
              type={showConfirmNewPassword ? 'text' : 'password'}
              required
              placeholder="Re-enter a new password"
            />
             <div
              className="absolute right-0 flex items-center h-full mr-2"
              onClick={() => {
                setShowConfirmNewPassword(!showConfirmNewPassword)
              }}
            >
              {showConfirmNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </div>
          </div>
          <FormErrorMessage
              message={translate(errors.confirm_password?.message ?? '')}
            />
        </div>

        <div className="flex">
          <Button type="submit" className="grow space-x-2" disabled={loading}>
            {loading && <CircleLoader size="sm" />}
            <span>
              {loading ? ` ${translate('processing')}...` : translate('submit')}
            </span>
          </Button>
        </div>
      </form>
    </>
  )
}

const PasswordChangedView = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const handleSubmit = () => {
    navigate(AppRoutePaths.auth.login)
  }
  return (
    <div className="flex flex-col">
      <img
        alt={translate('ace_logo')}
        className="light:hidden max-h-[180px] "
        src={AceScholarshipsLogo}
      />
      <h3 className="text-lg font-semibold text-gray-900 text-center mt-8 mb-4">
        {translate('your_password_is_changed')}
      </h3>
      <div className="text-sm text-gray-500 font-medium text-center mb-7">
        {translate('your_password_has_been_successfully_updated')}
        <br />
        {translate('your_accounts_security_isour_priority')}
      </div>
      <div className="flex justify-center">
        <Button onClick={handleSubmit}>{translate('log_in')}</Button>
      </div>
    </div>
  )
}

const querySchema = z.object({
  user_id: z.string(),
  email: z.string().email(),
  token: z.string(),
})
type QueryInvitation = z.infer<typeof querySchema>

async function decodeBase64Invitation(
  base64Str: string
): Promise<QueryInvitation> {
  
  const decodedUrlQueryString = atob(base64Str)
  
  const queryObj: { [key: string]: string } = {}
  

  decodedUrlQueryString.split('&').forEach(pair => {
    const [key, value] = pair.split('=')
    if (key && value !== undefined) {
      queryObj[key] = decodeURIComponent(value)
    }
  })
  
  return querySchema.parse(queryObj)
}